import { useState } from 'react';
import { useNavigate } from 'react-router';

import { Card, Button, Badge } from '@lib/ui';
import { toast } from '@lib/ui';
import type { WizardPersona } from './types';
import { StepByStepWizard } from './StepByStepWizard';
import { InterviewWizard } from './InterviewWizard';
import { TemplateWizard } from './TemplateWizard';
import { LivePreviewWizard } from './LivePreviewWizard';

// Icons
const StepsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className="size-8"
  >
    <path
      fillRule="evenodd"
      d="M2.625 6.75a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875 0A.75.75 0 0 1 8.25 6h12a.75.75 0 0 1 0 1.5h-12a.75.75 0 0 1-.75-.75ZM2.625 12a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0ZM7.5 12a.75.75 0 0 1 .75-.75h12a.75.75 0 0 1 0 1.5h-12A.75.75 0 0 1 7.5 12Zm-4.875 5.25a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875 0a.75.75 0 0 1 .75-.75h12a.75.75 0 0 1 0 1.5h-12a.75.75 0 0 1-.75-.75Z"
      clipRule="evenodd"
    />
  </svg>
);

const ChatIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className="size-8"
  >
    <path
      fillRule="evenodd"
      d="M4.848 2.771A49.144 49.144 0 0 1 12 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 0 1-3.476.383.39.39 0 0 0-.297.17l-2.755 4.133a.75.75 0 0 1-1.248 0l-2.755-4.133a.39.39 0 0 0-.297-.17 48.9 48.9 0 0 1-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97Z"
      clipRule="evenodd"
    />
  </svg>
);

const TemplateIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className="size-8"
  >
    <path
      fillRule="evenodd"
      d="M3 6a3 3 0 0 1 3-3h2.25a3 3 0 0 1 3 3v2.25a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6Zm9.75 0a3 3 0 0 1 3-3H18a3 3 0 0 1 3 3v2.25a3 3 0 0 1-3 3h-2.25a3 3 0 0 1-3-3V6ZM3 15.75a3 3 0 0 1 3-3h2.25a3 3 0 0 1 3 3V18a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3v-2.25Zm9.75 0a3 3 0 0 1 3-3H18a3 3 0 0 1 3 3V18a3 3 0 0 1-3 3h-2.25a3 3 0 0 1-3-3v-2.25Z"
      clipRule="evenodd"
    />
  </svg>
);

const PreviewIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className="size-8"
  >
    <path d="M18.375 2.25c-1.035 0-1.875.84-1.875 1.875v15.75c0 1.035.84 1.875 1.875 1.875h.75c1.035 0 1.875-.84 1.875-1.875V4.125c0-1.036-.84-1.875-1.875-1.875h-.75ZM9.75 8.625c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v11.25c0 1.035-.84 1.875-1.875 1.875h-.75a1.875 1.875 0 0 1-1.875-1.875V8.625ZM3 13.125c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v6.75c0 1.035-.84 1.875-1.875 1.875h-.75A1.875 1.875 0 0 1 3 19.875v-6.75Z" />
  </svg>
);

interface WizardOption {
  id: string;
  name: string;
  description: string;
  icon: () => React.JSX.Element;
  containerType: string;
  features: string[];
  color: string;
}

const WIZARD_OPTIONS: WizardOption[] = [
  {
    id: 'step-by-step',
    name: 'Step-by-Step Guided',
    description:
      'Traditional linear wizard with clear progression. Great for first-time users who want guidance through each step.',
    icon: StepsIcon,
    containerType: 'Full-page layout',
    features: [
      '5 clear steps',
      'Progress indicator',
      'Inline help & examples',
      'Clickable suggestions',
    ],
    color: 'primary',
  },
  {
    id: 'interview',
    name: 'Interview Style',
    description:
      'Chat-like conversational interface that feels like an interview. Questions appear one at a time with animated transitions.',
    icon: ChatIcon,
    containerType: 'Modal dialog',
    features: [
      'Conversational flow',
      'Help me answer button',
      'Skip & return later',
      'Previous answers sidebar',
    ],
    color: 'info',
  },
  {
    id: 'template',
    name: 'Template Based',
    description:
      'Start from pre-built archetypes and customize. Perfect for users who want a quick start with proven persona patterns.',
    icon: TemplateIcon,
    containerType: 'Drawer (right)',
    features: [
      '6 archetype templates',
      'Pre-filled values',
      'Side-by-side comparison',
      'Tone sliders',
    ],
    color: 'success',
  },
  {
    id: 'live-preview',
    name: 'Live Preview',
    description:
      'Split-screen editor with real-time content preview. See exactly how your persona will write as you build it.',
    icon: PreviewIcon,
    containerType: 'Split-screen',
    features: ['Instant preview', 'Multiple channels', 'Sample prompts', 'Completeness score'],
    color: 'warning',
  },
];

type ActiveWizard = 'step-by-step' | 'interview' | 'template' | 'live-preview' | null;

export default function WizardSelector() {
  const navigate = useNavigate();
  const [activeWizard, setActiveWizard] = useState<ActiveWizard>(null);

  const handleComplete = (persona: WizardPersona) => {
    toast.success(`Persona "${persona.name}" created successfully!`);
    setActiveWizard(null);
    // In a real app, this would save the persona
    console.log('Created persona:', persona);
  };

  const handleCancel = () => {
    setActiveWizard(null);
  };

  const handleBack = () => {
    navigate('/personas');
  };

  // Render full-page wizards
  if (activeWizard === 'step-by-step') {
    return <StepByStepWizard onComplete={handleComplete} onCancel={handleCancel} />;
  }

  if (activeWizard === 'live-preview') {
    return <LivePreviewWizard onComplete={handleComplete} onCancel={handleCancel} />;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Button variant="flat" onClick={handleBack} className="mb-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="size-5 mr-1"
            >
              <path
                fillRule="evenodd"
                d="M17 10a.75.75 0 0 1-.75.75H5.612l4.158 3.96a.75.75 0 1 1-1.04 1.08l-5.5-5.25a.75.75 0 0 1 0-1.08l5.5-5.25a.75.75 0 1 1 1.04 1.08L5.612 9.25H16.25A.75.75 0 0 1 17 10Z"
                clipRule="evenodd"
              />
            </svg>
            Back to Personas
          </Button>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Persona Creation Wizards
          </h1>
          <p className="mt-1 text-gray-500 dark:text-dark-300">
            Choose your preferred experience for creating a new persona
          </p>
        </div>
        <Badge variant="soft" color="info" className="self-start sm:self-auto">
          POC Demo
        </Badge>
      </div>

      {/* Info card */}
      <Card className="p-4 bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800">
        <p className="text-sm text-primary-800 dark:text-primary-200">
          This is a proof-of-concept demonstrating 4 different UX approaches for persona creation.
          Each wizard uses different container types, interaction patterns, and help systems.
          <strong className="block mt-2">
            Note: No data is persisted - this is UI demonstration only.
          </strong>
        </p>
      </Card>

      {/* Wizard options grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {WIZARD_OPTIONS.map((wizard) => {
          const IconComponent = wizard.icon;

          return (
            <Card
              key={wizard.id}
              skin="shadow"
              className="p-6 hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => setActiveWizard(wizard.id as ActiveWizard)}
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div
                  className={`shrink-0 p-3 rounded-lg bg-${wizard.color}-100 text-${wizard.color}-600 dark:bg-${wizard.color}-900/30 dark:text-${wizard.color}-400`}
                  style={{
                    backgroundColor: `var(--color-${wizard.color}-100)`,
                    color: `var(--color-${wizard.color}-600)`,
                  }}
                >
                  <IconComponent />
                </div>

                <div className="flex-1 min-w-0">
                  {/* Header */}
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {wizard.name}
                    </h3>
                    <Badge variant="outlined" color="neutral" className="shrink-0 text-xs">
                      {wizard.containerType}
                    </Badge>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-600 dark:text-dark-300 mb-4">
                    {wizard.description}
                  </p>

                  {/* Features */}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {wizard.features.map((feature) => (
                      <Badge
                        key={feature}
                        variant="soft"
                        color={wizard.color as 'primary' | 'info' | 'success' | 'warning'}
                        className="text-xs"
                      >
                        {feature}
                      </Badge>
                    ))}
                  </div>

                  {/* Action */}
                  <Button
                    color={wizard.color as 'primary' | 'info' | 'success' | 'warning'}
                    className="w-full"
                  >
                    Try This Wizard
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Comparison table */}
      <Card skin="bordered" className="overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 dark:bg-dark-700 border-b border-gray-200 dark:border-dark-600">
          <h2 className="font-semibold text-gray-900 dark:text-white">Comparison</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-dark-600">
                <th className="px-6 py-3 text-left font-medium text-gray-500 dark:text-dark-400">
                  Feature
                </th>
                <th className="px-4 py-3 text-center font-medium text-gray-500 dark:text-dark-400">
                  Step-by-Step
                </th>
                <th className="px-4 py-3 text-center font-medium text-gray-500 dark:text-dark-400">
                  Interview
                </th>
                <th className="px-4 py-3 text-center font-medium text-gray-500 dark:text-dark-400">
                  Template
                </th>
                <th className="px-4 py-3 text-center font-medium text-gray-500 dark:text-dark-400">
                  Live Preview
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-dark-600">
              <tr>
                <td className="px-6 py-3 text-gray-900 dark:text-dark-100">Container</td>
                <td className="px-4 py-3 text-center">Full page</td>
                <td className="px-4 py-3 text-center">Modal</td>
                <td className="px-4 py-3 text-center">Drawer</td>
                <td className="px-4 py-3 text-center">Split screen</td>
              </tr>
              <tr>
                <td className="px-6 py-3 text-gray-900 dark:text-dark-100">Progress tracking</td>
                <td className="px-4 py-3 text-center text-success-600">Yes</td>
                <td className="px-4 py-3 text-center text-success-600">Yes</td>
                <td className="px-4 py-3 text-center text-success-600">Yes</td>
                <td className="px-4 py-3 text-center text-gray-400">-</td>
              </tr>
              <tr>
                <td className="px-6 py-3 text-gray-900 dark:text-dark-100">Live preview</td>
                <td className="px-4 py-3 text-center text-gray-400">Step 5 only</td>
                <td className="px-4 py-3 text-center text-gray-400">Final step</td>
                <td className="px-4 py-3 text-center text-success-600">Yes</td>
                <td className="px-4 py-3 text-center text-success-600">Always</td>
              </tr>
              <tr>
                <td className="px-6 py-3 text-gray-900 dark:text-dark-100">Templates</td>
                <td className="px-4 py-3 text-center text-gray-400">-</td>
                <td className="px-4 py-3 text-center text-gray-400">-</td>
                <td className="px-4 py-3 text-center text-success-600">6 archetypes</td>
                <td className="px-4 py-3 text-center text-gray-400">-</td>
              </tr>
              <tr>
                <td className="px-6 py-3 text-gray-900 dark:text-dark-100">Best for</td>
                <td className="px-4 py-3 text-center text-xs">First-time users</td>
                <td className="px-4 py-3 text-center text-xs">Exploration</td>
                <td className="px-4 py-3 text-center text-xs">Quick start</td>
                <td className="px-4 py-3 text-center text-xs">Power users</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* Modal/Drawer wizards */}
      <InterviewWizard
        isOpen={activeWizard === 'interview'}
        onClose={handleCancel}
        onComplete={handleComplete}
      />

      <TemplateWizard
        isOpen={activeWizard === 'template'}
        onClose={handleCancel}
        onComplete={handleComplete}
      />
    </div>
  );
}
